import React, { useCallback, useMemo, useState, useEffect } from 'react'
// eslint-disable-next-line no-unused-vars
import { useSelector, useDispatch } from 'react-redux'
import { func } from 'prop-types'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/Drawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Table from '@groovehq/internal-design-system/lib/components/Table/Table'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import {
  selectOrderForId,
  selectCurrencyCode,
} from 'ducks/integrations/shopify/selectors'
import { selectCurrentTicketGqlId } from 'selectors/tickets'
import { selectIntegrationCredentialsForId } from 'ducks/integrations'

import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { moneyString } from 'util/currency'
import { debounce } from 'util/functions'
import doCalculateRefund from 'ducks/integrations/shopify/operations/doCalculateRefund'
import doCreateRefund from 'ducks/integrations/shopify/operations/doCreateRefund'

import { useForm, useController } from 'react-hook-form'

import RefundSummary from '../RefundSummary/RefundSummary'
import { styles as createOrderStyles } from '../CreateOrderDrawer/styles'
import useColumns from './useColumns'
import ReturnNotification from './ReturnNotification'
import {
  buildOrderItems,
  buildDefaultRefundEntity,
  buildRefundEntityFromShopifyCalculation,
} from './functions'
import { styles } from './styles'

const OrderItemTable = ({ control, loading, updateData }) => {
  const {
    field: { value: orderItems },
  } = useController({
    name: 'orderItems',
    control,
  })

  const columns = useColumns()

  return (
    <Table
      className={styles.table}
      columns={columns}
      data={loading ? [] : orderItems}
      loading={loading}
      updateData={updateData}
      css={[createOrderStyles.table, createOrderStyles.refundOrderTable]}
    />
  )
}

const Footer = ({ control, sending, onClose }) => {
  const {
    field: { value: refundEntity },
  } = useController({
    name: 'refundEntity',
    control,
  })

  const {
    field: { value: userRefundAmount },
  } = useController({
    name: 'userRefundAmount',
    control,
  })

  const {
    field: { value: isCalculating },
  } = useController({
    name: 'isCalculating',
    control,
  })

  const saveBtnText = useMemo(
    () => {
      if (sending) return 'Refunding...'
      if (isCalculating) return 'Calculating refund...'
      return `Refund ${moneyString({
        currencyCode: 'USD',
        amount: userRefundAmount || refundEntity.total,
      })}`
    },
    [isCalculating, refundEntity, userRefundAmount, sending]
  )

  return (
    <div className="grui text-center">
      {userRefundAmount !== null &&
        userRefundAmount !== refundEntity.total && (
          <MessageCard
            type="info"
            closeable
            className="grui my-10 mx-12 inline-flex"
          >
            The amount you’re refunding is different from the suggested refund
            total.
          </MessageCard>
        )}
      <ModalBtns
        saveBtnText={saveBtnText}
        tertiaryBtnText="Cancel"
        onClickTertiaryBtn={onClose}
        saveBtnDisabled={isCalculating || sending}
        saveBtnHtmlType="submit"
      />
    </div>
  )
}

const RefundOrder = ({
  drawerResourceId,
  drawerIntegrationId,
  onClose,
  onExit,
  visible = true,
}) => {
  let isVisible = visible

  const integration = useSelector(state => {
    return selectIntegrationCredentialsForId(state, {
      id: drawerIntegrationId,
    })
  })

  const conversationId = useSelector(selectCurrentTicketGqlId)
  const currencyCode = useSelector(selectCurrencyCode)

  // we pass the order id and then get it from the state - this allows the id to be stored
  // in the url and survices a refresh
  const order = useSelector(state =>
    selectOrderForId(state, { orderId: drawerResourceId })
  )

  const refundCurrencyCode = (order && order.refundCurrencyCode) || currencyCode

  const dispatch = useDispatch()

  const [sending, setSending] = useState(false)
  const [calculating, setCalculating] = useState(false)
  const [errorFields, setErrorFields] = useState({})
  const [loading, setLoading] = useState(true)

  const defaultOrderItems = useMemo(
    () => {
      return buildOrderItems(order, refundCurrencyCode)
    },
    [order, refundCurrencyCode]
  )

  const defaultRefundEntity = useMemo(() => {
    return buildDefaultRefundEntity()
  }, [])

  const {
    handleSubmit,
    // formState: { isValid },
    control,
    setValue,
    getValues,
    register,
    reset,
  } = useForm({
    mode: 'all',
    defaultValues: {
      orderItems: defaultOrderItems,
      isCalculating: false,
      restock: true,
      sendNotification: true,
      refundShipping: 0,
      refundAmount: null,
      userRefundAmount: null,
      refundReason: '',
      refundEntity: defaultRefundEntity,
    },
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const executeCalculation = useCallback(
    debounce((newOrderItems, shipping) => {
      const restock = getValues('restock')
      dispatch(
        doCalculateRefund(integration.id, order.id, {
          orderItems: newOrderItems,
          shipping,
          restock,
          order,
        })
      ).then(data => {
        const newRefundEntity = buildRefundEntityFromShopifyCalculation(
          data,
          shipping
        )
        setValue('refundEntity', newRefundEntity)
        setValue('isCalculating', false)
      })
    }, 500),
    [setCalculating, dispatch, integration, order]
  )

  const runCalculation = useCallback(
    (newOrderItems, shipping) => {
      // setCalculating(true)
      setValue('isCalculating', true)
      executeCalculation(newOrderItems, shipping)
    },
    [setValue, executeCalculation]
  )

  useEffect(
    () => {
      if (order && loading) {
        const newOrderItems = buildOrderItems(order, refundCurrencyCode)
        reset({
          ...getValues(),
          orderItems: newOrderItems,
        })
        setValue('orderItems', newOrderItems)
        setLoading(false)

        runCalculation(newOrderItems, getValues('refundShipping'))
      }
    },
    [
      order,
      setValue,
      loading,
      setLoading,
      getValues,
      runCalculation,
      reset,
      refundCurrencyCode,
    ]
  )

  const onShippingChange = useCallback(
    newShipping => {
      setValue('refundShipping', newShipping)
      runCalculation(getValues('orderItems'), getValues('refundShipping'))
    },
    [setValue, getValues, runCalculation]
  )

  // Custom function for updating table data
  const updateData = useCallback(
    (index, id, value) => {
      const orderItems = getValues('orderItems')
      // eslint-disable-next-line no-console
      console.log({ index, id, value, orderItems })
      if (id === 'refundQuantity') {
        const orderItem = { ...orderItems[index] }
        const newRefundQuantity = parseInt(value, 10)
        if (newRefundQuantity > orderItem.quantity) return
        orderItem.refundQuantity = newRefundQuantity
        const newOrderItems = [].concat(orderItems)
        newOrderItems[index] = orderItem
        setValue('orderItems', newOrderItems)

        runCalculation(newOrderItems, getValues('refundShipping'))
      }
    },
    [getValues, runCalculation, setValue]
  )

  const handleSubmitable = useCallback(
    ({ target, rule = {} }) => {
      if (rule.number) {
        const numberValidity = target.checkValidity()
        setErrorFields({
          ...errorFields,
          [target.name]: { error: !numberValidity },
        })
      }
    },
    [errorFields]
  )

  const onUserRefundAmountChange = useCallback(
    newVal => {
      setValue('userRefundAmount', newVal)
    },
    [setValue]
  )

  const footer = (
    <Footer
      sending={sending}
      control={control}
      onClose={onClose}
      currencyCode={refundCurrencyCode}
    />
  )

  const onSubmit = useCallback(
    async data => {
      const orderItems = getValues('orderItems')
      const refundEntity = getValues('refundEntity')
      const datas = { ...data, orderItems, refundEntity }
      const { transactions } = refundEntity
      const payload = {
        currencyCode: refundCurrencyCode,
        notes: datas.refundReason,
        sendNotification: datas.sendNotification,
        lineItems: datas.orderItems,
        restock: datas.restock,
        shippingAmount: datas.refundShipping,
      }
      if (transactions && transactions.length === 1) {
        const transaction = transactions[0]
        payload.transactionInformation = true
        payload.transactionAmount = parseFloat(transaction.amount)
        payload.transactionGateway = transaction.gateway
        payload.transactionOrderId = transaction.order_id
        payload.transactionParentId = transaction.parent_id
      }

      if (datas.restock) {
        payload.lineItems = payload.lineItems.map(item => {
          const restock = refundEntity.restocks[item.id]
          return {
            ...item,
            locationId: restock.locationId,
            restockType: restock.restockType,
          }
        })
      }
      payload.conversationId = conversationId

      setSending(true)
      await dispatch(doCreateRefund(integration.id, order.id, payload))
      setSending(false)
      onClose()
    },
    [
      dispatch,
      integration,
      order,
      onClose,
      getValues,
      refundCurrencyCode,
      conversationId,
    ]
  )

  const DrawerForm = useCallback(
    props => <form onSubmit={handleSubmit(onSubmit)} {...props} />,
    [handleSubmit, onSubmit]
  )

  // If these have note loaded yet, do not show drawer
  if (!integration || !order) isVisible = false

  return (
    <Drawer
      title="Refund order"
      footer={isVisible ? footer : null}
      onClose={onExit}
      open
      isLoading={!isVisible}
      css={text.styles.textDark}
      size="wide"
      container={DrawerForm}
    >
      <ReturnNotification
        integration={integration}
        orderId={drawerResourceId}
      />
      <OrderItemTable
        control={control}
        loading={loading}
        updateData={updateData}
      />
      <RefundSummary
        control={control}
        currencyCode={refundCurrencyCode}
        onSubmitable={handleSubmitable}
        onShippingChange={onShippingChange}
        className="grui mt-10"
        register={register}
        isCalculating={calculating}
        onUserRefundAmountChange={onUserRefundAmountChange}
      />
    </Drawer>
  )
}

RefundOrder.propTypes = {
  onClose: func.isRequired,
}

RefundOrder.defaultProps = {}

export default RefundOrder
